import {
  GIF,
  JPEG,
  PNG,
  PresignedUrl,
  unitChangeForByte,
  WEBP,
} from '@/domain/models/Image';
import {
  gmoShopInformationRequired,
  PAY_TYPE,
  PayType,
  payType,
  SitePayTypeSetting,
  SitePayTypeSettingKey,
} from '@/domain/models/Order';
import { OrderMethod } from '@/domain/models/Order/orderMethod';
import {
  DisplaySpecialInstruction,
  IsAuthAvailable,
  SiteListResponse,
  SiteStatus,
  TAX_ROUND_TYPE,
  TaxRoundType,
} from '@/services/Site/hooks/list/useSiteList';

const qrPayTypeMap = () => {
  return [
    {
      value: PAY_TYPE.PAYPAY,
      name: payType.PayPay,
    },
    {
      value: PAY_TYPE.D_PAY,
      name: payType.D_PAY,
    },
    {
      value: PAY_TYPE.RAKUTEN_PAY,
      name: payType.RAKUTEN_PAY,
    },
    {
      value: PAY_TYPE.AU_PAY,
      name: payType.AU_PAY,
    },
    {
      value: PAY_TYPE.AMAZON_PAY,
      name: payType.AMAZON_PAY,
    },
    {
      value: PAY_TYPE.MERPAY,
      name: payType.MERPAY,
    },
  ];
};

export const takeoutSitePayTypeMap = [
  {
    value: PAY_TYPE.IN_STORE_PAYMENT,
    name: payType.IN_STORE_PAYMENT,
  },
  {
    value: PAY_TYPE.CREDIT_CARD,
    name: payType.CREDIT_CARD,
  },
  {
    value: PAY_TYPE.APPLE_PAY,
    name: payType.APPLE_PAY,
  },
];

export const eatinSitePayTypeMap = [
  {
    value: PAY_TYPE.IN_STORE_PAYMENT,
    name: payType.IN_STORE_PAYMENT,
  },
  {
    value: PAY_TYPE.CREDIT_CARD,
    name: payType.CREDIT_CARD,
  },
  {
    value: PAY_TYPE.APPLE_PAY,
    name: payType.APPLE_PAY,
  },
];

export const deliveryUberSitePayTypeMap = [
  {
    value: PAY_TYPE.CREDIT_CARD,
    name: payType.CREDIT_CARD,
  },
  {
    value: PAY_TYPE.APPLE_PAY,
    name: payType.APPLE_PAY,
  },
];

export const deliveryInHouseSitePayTypeMap = [
  {
    value: PAY_TYPE.CREDIT_CARD,
    name: payType.CREDIT_CARD,
  },
  {
    value: PAY_TYPE.APPLE_PAY,
    name: payType.APPLE_PAY,
  },
  {
    value: PAY_TYPE.ON_DELIVERY,
    name: payType.ON_DELIVERY,
  },
];

const DELIVERY_CART_ITEM_LIMIT = {
  MIN: 1,
  MAX: 99,
} as const;

export const deliveryCartItemLimitOptions = Array.from(
  { length: DELIVERY_CART_ITEM_LIMIT.MAX - DELIVERY_CART_ITEM_LIMIT.MIN + 1 },
  (_, i) => DELIVERY_CART_ITEM_LIMIT.MIN + i
);

export type PayTypeMap = {
  value: PayType;
  name: string;
};

export function sitePayTypeMapByPayTypeSettingKey(
  payTypeSettingKey: SitePayTypeSettingKey
): PayTypeMap[] {
  if (payTypeSettingKey === 'TAKE_OUT') {
    return [...takeoutSitePayTypeMap, ...qrPayTypeMap()];
  }
  if (payTypeSettingKey === 'EAT_IN') {
    return [...eatinSitePayTypeMap, ...qrPayTypeMap()];
  }
  if (payTypeSettingKey === 'DELIVERY_UBER') {
    return [...deliveryUberSitePayTypeMap, ...qrPayTypeMap()];
  }
  if (payTypeSettingKey === 'DELIVERY_IN_HOUSE') {
    return [...deliveryInHouseSitePayTypeMap, ...qrPayTypeMap()];
  }
  return [];
}

export const orderMethodAndSitePayTypeSettingKeysCombinations = Object.entries({
  TAKE_OUT: ['TAKE_OUT'],
  EAT_IN: ['EAT_IN'],
  DELIVERY: ['DELIVERY_UBER', 'DELIVERY_IN_HOUSE'],
});

export const taxRoundTypeMap = [
  {
    value: TAX_ROUND_TYPE.ROUND_UP,
    label: '切り上げ',
  },
  {
    value: TAX_ROUND_TYPE.ROUND_DOWN,
    label: '切り捨て',
  },
  {
    value: TAX_ROUND_TYPE.ROUND_OFF,
    label: '四捨五入',
  },
];

export const privateStatus = 0;
export const publicStatus = 1;
export const restrictedStatus = 2;
export const maintenanceStatus = 3;

export const siteStatusMap = {
  [privateStatus]: {
    color: 'gray',
    name: '公開停止中',
  },
  [publicStatus]: {
    color: 'green',
    name: '公開中',
  },
  [restrictedStatus]: {
    color: 'red',
    name: '限定公開',
  },
  [maintenanceStatus]: {
    color: 'orange',
    name: 'メンテナンス中',
  },
} as const;

const siteUrl = {
  key: 'siteUrl',
  label: 'サイトURL',
} as const;
const status = {
  key: 'status',
  label: '公開・非公開設定',
} as const;
const password = {
  key: 'password',
  label: 'パスワード',
} as const;
const tax = {
  key: 'tax',
  label: '消費税の端数計算設定',
} as const;
const gmoShopName = {
  key: 'gmoShopName',
  label: '表示用の加盟店様名',
} as const;
const gmoShopNameKana = {
  key: 'gmoShopNameKana',
  label: '表示用の加盟店様名（全角カナのみ）',
} as const;
const gmoShopNameAlphabet = {
  key: 'gmoShopNameAlphabet',
  label: '表示用の加盟店様名（英名）',
} as const;
const gmoShopNameShort = {
  key: 'gmoShopNameShort',
  label: '表示用の加盟店様名（略称）',
} as const;
const gmoShopContactName = {
  key: 'gmoShopContactName',
  label: '加盟店様の問い合わせ先名称',
} as const;
const gmoShopContactEmail = {
  key: 'gmoShopContactEmail',
  label: '加盟店様の問い合わせ先メールアドレス',
} as const;
const gmoShopContactPhone = {
  key: 'gmoShopContactPhone',
  label: '加盟店様の問い合わせ先電話番号',
} as const;
const gmoShopContactOpeningHours = {
  key: 'gmoShopContactOpeningHours',
  label: '加盟店様の問い合わせ窓口の営業時間',
} as const;
const logoUrl = {
  key: 'logoUrl',
  label: 'ユーザー画面ヘッダーロゴ',
} as const;
const faviconUrl = {
  key: 'faviconUrl',
  label: 'ファビコン画像',
} as const;
const primaryColor = {
  key: 'primaryColor',
  label: 'メインカラー',
} as const;
const faqUrl = {
  key: 'faqUrl',
  label: 'よくある質問',
} as const;
const inquiryUrl = {
  key: 'inquiryUrl',
  label: 'お問い合わせ',
} as const;
const termUrl = {
  key: 'termUrl',
  label: '利用規約',
} as const;
const sctlUrl = {
  key: 'sctlUrl',
  label: '特定商取引に基づく記載',
} as const;
const privacyUrl = {
  key: 'privacyUrl',
  label: 'プライバシーポリシー',
} as const;
const title = {
  key: 'title',
  label: 'サイトタイトル',
} as const;
const description = {
  key: 'description',
  label: 'サイト説明文',
} as const;
const googleTagId = {
  key: 'googleTagId',
  label: 'GoogleタグID',
} as const;
const notificationText = {
  key: 'notificationText',
  label: 'メール通知の文面にカスタムテキストを追加',
} as const;
const siteBannersLinkUrl = {
  key: 'siteBannersLinkUrl',
  label: 'スライドショーのリンク先URL',
} as const;
const orderTypeDisplayName = {
  key: 'orderTypeDisplayName',
  label: '注文方式の表示名設定',
};
const customFieldName = {
  key: 'customFieldName',
  label: '項目名',
} as const;
const customFieldDescription = {
  key: 'customFieldDescription',
  label: '説明（入力欄に表示される文言）',
} as const;
const customFieldMinimum = {
  key: 'customFieldMinimum',
  label: '最低文字数',
} as const;
const customFieldMaximum = {
  key: 'customFieldMaximum',
  label: '最大文字数',
} as const;
const customFieldRegex = {
  key: 'customFieldRegex',
  label: '正規表現',
} as const;
const deliveryMinimumAmount = {
  key: 'deliveryMinimumAmount',
  label: '配達最低金額',
} as const;

export const siteForm = {
  siteUrl,
  status,
  password,
  tax,
  gmoShopName,
  gmoShopNameKana,
  gmoShopNameAlphabet,
  gmoShopNameShort,
  gmoShopContactName,
  gmoShopContactEmail,
  gmoShopContactPhone,
  gmoShopContactOpeningHours,
  logoUrl,
  faviconUrl,
  primaryColor,
  faqUrl,
  inquiryUrl,
  termUrl,
  sctlUrl,
  privacyUrl,
  title,
  description,
  googleTagId,
  notificationText,
  siteBannersLinkUrl,
  orderTypeDisplayName,
  customFieldName,
  customFieldDescription,
  customFieldMinimum,
  customFieldMaximum,
  customFieldRegex,
  deliveryMinimumAmount,
} as const;

export type CustomField = {
  id: string;
  site_id: string;
  name: string;
  takeout: boolean;
  eat_in: boolean;
  delivery: boolean;
  required: boolean;
  custom_field_type: 'TEXT' | 'TEXTAREA';
  description: string;
  minimum?: number;
  maximum?: number;
  regex?: string;
};

export type GMOShopPaymentMethodConfig = {
  amazon_pay_enabled: boolean;
  apple_pay_enabled: boolean;
  au_pay_enabled: boolean;
  credit_card_enabled: boolean;
  d_pay_enabled: boolean;
  merpay_enabled: boolean;
  paypay_enabled: boolean;
  rakuten_pay_enabled: boolean;
};

export const isAvailablePayType = (
  targetPayType: PayType,
  paymentMethodConfig: GMOShopPaymentMethodConfig
): boolean => {
  if (targetPayType === PAY_TYPE.CREDIT_CARD) {
    return paymentMethodConfig.credit_card_enabled;
  }
  if (targetPayType === PAY_TYPE.APPLE_PAY) {
    return paymentMethodConfig.apple_pay_enabled;
  }
  if (targetPayType === PAY_TYPE.PAYPAY) {
    return paymentMethodConfig.paypay_enabled;
  }
  if (targetPayType === PAY_TYPE.D_PAY) {
    return paymentMethodConfig.d_pay_enabled;
  }
  if (targetPayType === PAY_TYPE.RAKUTEN_PAY) {
    return paymentMethodConfig.rakuten_pay_enabled;
  }
  if (targetPayType === PAY_TYPE.AU_PAY) {
    return paymentMethodConfig.au_pay_enabled;
  }
  if (targetPayType === PAY_TYPE.AMAZON_PAY) {
    return paymentMethodConfig.amazon_pay_enabled;
  }
  if (targetPayType === PAY_TYPE.MERPAY) {
    return paymentMethodConfig.merpay_enabled;
  }
  return true;
};

export const shouldShowGMOSettingPendingMessage = (
  targetPayType: PayType,
  paymentMethodConfig: GMOShopPaymentMethodConfig,
  gmoSettingCompleted: boolean
): boolean => {
  if (
    gmoSettingCompleted ||
    !isAvailablePayType(targetPayType, paymentMethodConfig)
  ) {
    return false;
  }
  if (targetPayType === PAY_TYPE.CREDIT_CARD) {
    return true;
  }
  return (
    targetPayType === PAY_TYPE.APPLE_PAY &&
    !isAvailablePayType(PAY_TYPE.CREDIT_CARD, paymentMethodConfig)
  );
};

export const initializeGMOShopPaymentMethodConfig = (
  config: GMOShopPaymentMethodConfig
): GMOShopPaymentMethodConfig => ({
  amazon_pay_enabled: config.amazon_pay_enabled,
  apple_pay_enabled: config.apple_pay_enabled,
  au_pay_enabled: config.au_pay_enabled,
  credit_card_enabled: config.credit_card_enabled,
  d_pay_enabled: config.d_pay_enabled,
  merpay_enabled: config.merpay_enabled,
  paypay_enabled: config.paypay_enabled,
  rakuten_pay_enabled: config.rakuten_pay_enabled,
});

export function gmoShopForUpdate<T>(
  payTypeSettings: SitePayTypeSetting[],
  gmoShop: T
): T | null {
  return gmoShopInformationRequired(payTypeSettings) ? gmoShop : null;
}

export type Site = {
  id: string;
  url: string;
  title: string;
  description: string;
  logo: PresignedUrl;
  favicon: PresignedUrl;
  primary_color: string;
  faq_url: string;
  inquiry_url: string;
  term_url: string;
  sctl_url: string;
  privacy_url: string;
  status: SiteStatus;
  password: string;
  pay_type_settings: SitePayTypeSetting[];
  order_methods: OrderMethod[];
  tax_round_type: TaxRoundType;
  gmo_shop_payment_method_config: GMOShopPaymentMethodConfig;
  gmo_setting_completed: boolean;
  google_tag_ids: string[];
  display_special_instruction: DisplaySpecialInstruction;
  is_auth_available: IsAuthAvailable;
  notification_text: string;
  delivery_minimum_amount: number;
  delivery_cart_item_limit: number | null;
  use_three_d_secure2: boolean;
  is_three_d_secure2_setting_available: boolean;
  site_banners: {
    id: string;
    image_url: string;
    link_url: string | null;
    file?: File;
    presigned_url: string;
    sort_index: number;
  }[];
  gmo_shop: {
    name: string;
    name_kana: string;
    name_alphabet: string;
    name_short: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    contact_opening_hours: string;
    contact_closing_hours: string;
  };
  custom_order_method_text: {
    takeout: {
      is_custom: string;
      text: string | null;
      button_text: string | null;
    };
    eat_in: {
      is_custom: string;
      text: string | null;
      button_text: string | null;
    };
    delivery: {
      is_custom: string;
      text: string | null;
      button_text: string | null;
    };
  };
  custom_fields: CustomField[];
};

export const initializeSites = (resSites: SiteListResponse[]): Site[] => {
  const buildSites: Site[] = resSites.map((site) => {
    return {
      ...site,
      password: site.password ?? '',
      logo: {
        image_url: site.logo_url ?? '',
        presigned_url: '',
      },
      favicon: {
        image_url: site.favicon_url ?? '',
        presigned_url: '',
      },
      faq_url: site.faq_url ?? '',
      inquiry_url: site.inquiry_url ?? '',
      primary_color: site.primary_color || '#000000',
      google_tag_ids: site.google_tag_ids,
      display_special_instruction: site.display_special_instruction ? 1 : 0,
      is_auth_available: site.is_auth_available ? 1 : 0,
      use_three_d_secure2: site.use_three_d_secure2 ?? false,
      is_three_d_secure2_setting_available:
        site.is_three_d_secure2_setting_available ?? false,
      gmo_shop_payment_method_config: initializeGMOShopPaymentMethodConfig(
        site.gmo_shop_payment_method_config
      ),
      site_banners:
        site.site_banners.length === 0
          ? [
              {
                image_url: '',
                id: '',
                sort_index: 0,
                link_url: null,
                presigned_url: '',
              },
            ]
          : site.site_banners.map((banner) => {
              return {
                ...banner,
                presigned_url: '',
              };
            }),
      gmo_shop: {
        name: site.gmo_shop?.name ?? '',
        name_kana: site.gmo_shop?.name_kana ?? '',
        name_alphabet: site.gmo_shop?.name_alphabet ?? '',
        name_short: site.gmo_shop?.name_short ?? '',
        contact_name: site.gmo_shop?.contact_name ?? '',
        contact_email: site.gmo_shop?.contact_email ?? '',
        contact_phone: site.gmo_shop?.contact_phone ?? '',
        contact_opening_hours: site.gmo_shop?.contact_opening_hours ?? '00:00',
        contact_closing_hours: site.gmo_shop?.contact_closing_hours ?? '00:00',
        custom_fields: site.custom_fields ?? [],
      },
      custom_order_method_text: {
        takeout: {
          is_custom: site.custom_order_method_text?.takeout?.is_custom
            ? '1'
            : '0',
          text: site.custom_order_method_text?.takeout?.text ?? '',
          button_text:
            site.custom_order_method_text?.takeout?.button_text ?? '',
        },
        eat_in: {
          is_custom: site.custom_order_method_text?.eat_in?.is_custom
            ? '1'
            : '0',
          text: site.custom_order_method_text?.eat_in?.text ?? '',
          button_text: site.custom_order_method_text?.eat_in?.button_text ?? '',
        },
        delivery: {
          is_custom: site.custom_order_method_text?.delivery?.is_custom
            ? '1'
            : '0',
          text: site.custom_order_method_text?.delivery?.text ?? '',
          button_text:
            site.custom_order_method_text?.delivery?.button_text ?? '',
        },
      },
    };
  });
  return buildSites;
};

const maxSize = 1048576 as const; // 1MB
export const IMAGE_RULE = {
  LOGO: {
    maxSize,
    allowedImageTypes: [JPEG.type, PNG.type, WEBP.type, GIF.type],
    allowedImageFormats: [JPEG.name, PNG.name, WEBP.name, GIF.name],
  },
  FAVICON: {
    maxSize,
    allowedImageTypes: [PNG.type],
    allowedImageFormats: [PNG.name],
  },
  BANNER: {
    maxSize,
    allowedImageTypes: [JPEG.type, PNG.type, WEBP.type, GIF.type],
    allowedImageFormats: [JPEG.name, PNG.name, WEBP.name, GIF.name],
  },
  ITEM_IMAGE: {
    minWidth: 320 as const,
    minHeight: 320 as const,
    maxSize,
    allowedImageTypes: [JPEG.type, PNG.type, WEBP.type, GIF.type],
    allowedImageFormats: [JPEG.name, PNG.name, WEBP.name, GIF.name],
  },
};

export const validateImageCapacity = {
  check: (size: number, maxSize: number): boolean => {
    return size > maxSize;
  },
  message: (maxSize: number): string => {
    const text = unitChangeForByte(maxSize);
    return `${text}以下の画像を使用してください。`;
  },
};
